import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useFavourites } from '../../context/FavouritesContext';
import { useToast } from '../../context/ToastContext';
/* ---------------------------------------------------------------------------
   Local Role enum
   ---------------------------------------------------------------------------
   We define the Role enum here to avoid bundler resolution issues with the
   shared package during the production build. Keep the string literals in
   sync with the backend & shared enums.
--------------------------------------------------------------------------- */
enum Role {
  ADMIN = 'ADMIN',
  DEVELOPER = 'DEVELOPER',
}

// Icons
import {
  HomeIcon,
  PlusIcon,
  StarIcon,
  UserIcon,
  AdjustmentsHorizontalIcon,
  ArrowRightOnRectangleIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../../context/ThemeContext';
import { XMarkIcon } from '@heroicons/react/24/outline';

const MainLayout: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { favourites, addFavourite, removeFavourite } = useFavourites();
  const { showToast } = useToast();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Navigation items
  const navItems = [
    { to: '/', icon: <HomeIcon className="w-5 h-5" />, label: 'Dashboard' },
    { to: '/snippets', icon: <HomeIcon className="w-5 h-5" />, label: 'Snippets' },
    {
      to: '/favourites',
      icon: <StarIcon className="w-5 h-5" />,
      label: 'Favourites',
    },
    { to: '/editor', icon: <PlusIcon className="w-5 h-5" />, label: 'New Snippet' },
  ];

  // Admin-only navigation items
  const adminNavItems =
    user?.role === Role.ADMIN
      ? [{ to: '/admin', icon: <AdjustmentsHorizontalIcon className="w-5 h-5" />, label: 'Admin' }]
      : [];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header/Navbar */}
      <header className="navbar">
        <div className="container mx-auto flex justify-between items-center">
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Snippix"
              className="h-8 w-auto"
              onError={e => {
                (e.currentTarget as HTMLImageElement).src = '/logo.svg';
              }}
            />
          </NavLink>

          {/* Mobile menu button */}
          <button
            className="lg:hidden btn-ghost rounded-full p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isMobileMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
              />
            </svg>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4">
            {navItems.concat(adminNavItems).map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'text-primary bg-dark-700'
                      : 'text-gray-300 hover:text-white hover:bg-dark-700'
                  }`
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="btn-ghost rounded-full p-2"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? (
                <SunIcon className="w-5 h-5 text-yellow-400" />
              ) : (
                <MoonIcon className="w-5 h-5 text-gray-300" />
              )}
            </button>

            {/* User menu */}
            {isAuthenticated && (
              <div className="relative group">
                <button className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-dark-700">
                  <UserIcon className="w-5 h-5" />
                  <span>{user?.name}</span>
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-dark-800 border border-dark-600 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="p-3 border-b border-dark-600">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-gray-400">{user?.email}</p>
                    <p className="text-xs text-primary mt-1">{user?.role}</p>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-lg text-gray-300 hover:text-white hover:bg-dark-700"
                    >
                      <ArrowRightOnRectangleIcon className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </nav>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-dark-800 border-b border-dark-600"
          >
            <nav className="container mx-auto py-4 px-6 flex flex-col gap-2">
              {navItems.concat(adminNavItems).map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      isActive
                        ? 'text-primary bg-dark-700'
                        : 'text-gray-300 hover:text-white hover:bg-dark-700'
                    }`
                  }
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              ))}

              <div className="flex items-center justify-between mt-2 pt-2 border-t border-dark-600">
                {/* Theme toggle */}
                <button
                  onClick={toggleTheme}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-dark-700"
                >
                  {theme === 'dark' ? (
                    <>
                      <SunIcon className="w-5 h-5 text-yellow-400" />
                      <span>Light mode</span>
                    </>
                  ) : (
                    <>
                      <MoonIcon className="w-5 h-5" />
                      <span>Dark mode</span>
                    </>
                  )}
                </button>

                {/* Logout button */}
                {isAuthenticated && (
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-dark-700"
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5" />
                    <span>Logout</span>
                  </button>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <main className="flex-grow container mx-auto px-4 py-6 mt-16 pb-24">
        <Outlet />
      </main>

      {/* Favourites dock */}
      {isAuthenticated && (
        <div
          className="favourites-dock"
          onDragOver={e => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={async e => {
            e.preventDefault();
            const id = e.dataTransfer.getData('text/snippet-id');
            if (id) {
              const ok = await addFavourite(id);
              if (ok) showToast?.('Added to favourites', 'success');
            }
          }}
        >
          <div className="container mx-auto flex items-center gap-4">
            {/* Clickable link to favourites page */}
            <NavLink
              to="/favourites"
              className="flex items-center gap-2 text-primary hover:underline"
            >
              <StarIcon className="w-5 h-5" />
              <span className="font-medium">Favourites</span>
            </NavLink>
            {/* --------------------------------------------------------------------
                 Guard against undefined / non-array values
                 -------------------------------------------------------------------- */}
            {!Array.isArray(favourites) || favourites.length === 0 ? (
              <p className="text-gray-500 text-sm">Drag snippets here to add to favourites</p>
            ) : (
              <div className="flex items-center gap-3 overflow-x-auto overflow-y-hidden pb-2 px-1">
                {Array.isArray(favourites) &&
                  favourites.slice(0, 5).map(snippet => (
                    <motion.div
                      key={snippet.id}
                      className="glass-card relative min-w-48 px-4 py-3 cursor-pointer"
                      onClick={() => navigate(`/snippets/${snippet.id}`)}
                    >
                      {/* remove favourite button */}
                      <button
                        aria-label="Remove from favourites"
                        className="absolute top-1 right-1 text-gray-400 hover:text-red-400"
                        onClick={e => {
                          e.stopPropagation();
                          removeFavourite(snippet.id);
                        }}
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                      <h4 className="text-sm font-medium truncate">{snippet.title}</h4>
                      <div className="flex items-center gap-1 mt-1">
                        {snippet.tags.slice(0, 2).map(tag => (
                          <span key={tag.id} className="tag text-xs">
                            {tag.name}
                          </span>
                        ))}
                        {snippet.tags.length > 2 && (
                          <span className="text-xs text-gray-500">+{snippet.tags.length - 2}</span>
                        )}
                      </div>
                    </motion.div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MainLayout;
